import { createContext, useContext, type ReactNode } from 'react';
import { PopupDialog } from '@vigeo/ui';
import { CLOSING, useRecordingRow, type RecordingRow } from './useRecordingRow.js';

/**
 * Shares one open row across a checklist, and owns the discard prompt (PG15, PG9).
 *
 * The rules PG15 adds are about the rows as a set — one form open at a time, and switching away
 * from typing asks first — so a provider carries them. `useRecordingRow` decides what a switch
 * means; this puts it where every row can reach it and renders the one question it can ask.
 *
 * THE PROMPT IS A DIALOG EVEN THOUGH THE FORM IS NOT, and that is PG15's own shape: the form
 * moved into the page because it has fourteen or fifteen fields and PG10 sends anything over five
 * there. "Discard what you typed?" is one question with two answers, which is what a dialog is
 * for — and `PopupDialog` already implements the discard wording, button order and keyboard rules
 * from card 14, so asking it any other way would be a second place those rules live.
 */
const RecordingContext = createContext<RecordingRow | null>(null);

/**
 * Deliberately NOT throwing when absent. A row rendered outside a provider still has to work —
 * it simply has nothing to coordinate with — and a throw would turn "this list has no provider
 * yet" into a blank screen for HR. The fallback opens and closes itself and never prompts,
 * which is exactly the behaviour a lone row had before PG15.
 */
export function useRecording(): RecordingRow | null {
  return useContext(RecordingContext);
}

export function RecordingRowProvider({ children }: { readonly children: ReactNode }) {
  const recording = useRecordingRow();
  const asking = recording.askingFor !== null;
  return (
    <RecordingContext.Provider value={recording}>
      {children}
      <PopupDialog
        open={asking}
        title="Discard what you typed?"
        startOn="cancel"
        /* Esc, the X and a click outside all mean "I did not mean to leave", which is Keep
           editing — the safe answer, and the one that keeps what they wrote. */
        onClose={recording.keepEditing}
        cancelLabel="Keep editing"
        primary={{ label: 'Discard', onClick: recording.discard }}
      >
        {recording.askingFor === CLOSING
          ? 'This form has not been recorded. Closing it loses what you have typed.'
          : 'This form has not been recorded. Opening another row loses what you have typed.'}
      </PopupDialog>
    </RecordingContext.Provider>
  );
}
